import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import classes from './Expenses.module.scss';
import TNEDashboardHeader from '../../common/TNEDashboardHeader/TNEDashboardHeader';
import { useOnScreen, useQuery, useWindowSize, usePrevious } from '../../../utils/hooks';
import {
  appAreas,
  contractorExpensesFilters,
  globalPageSize,
  responsiveSizeTimesheet,
  approverExpensesFilters,
  timesheetsStatuses,
  appNotificationsTypes
} from '../../../utils/constants';
import CustomTable from '../../common/Table/CustomTable/CustomTable';
import TNECardsList from '../../common/TNECardsList/TNECardsList';
import { columns } from './helpers';
import { getExpenses, getNextExpenses, changeExpensesStatus, deleteClaim } from '../../../actions/expenses';
import StyledPaginationPanel from '../../common/StyledPaginationPanel/StyledPaginationPanel';
import { AddGradientIcon } from '../../common/Icons';
import { checkRoleInLS, basicErrorHandler } from '../../../utils/helpers';
import MainButton from '../../common/buttons/MainButton';
import colors from '../../../scss/colors';
import BaseModal from '../../common/ModalWindows/BaseModal';
import {
  SavedPopUp,
  UnsavedChanges,
  DeletePopUp,
  FinalModal,
  ApproveAllFinalModal,
  ConfirmationModal,
  RejectModal
} from '../expense/components/Modals/Modals';
import { tneAxiosClient } from '../../../api/apiSettings';
import WithCloseIcon from '../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import { getTimeExpenseValidation } from '../../../actions/timesheet';
import { clearExpense } from '../../../actions/expense';
import TnEListSkeleton from '../../common/Skeletons/TnEListSkeleton';
import { getOptionsForFilters } from '../../../actions/filtersOptionsFromApi';
import { buttonVariants } from '../../common/buttons/types';
import EmptyRecord from '../../common/EmptyRecordComponent/EmptyRecord';
import ExpensesEmptyIcon from '../../common/Icons/ExpensesEmptyIcon';
import { setNotification, removeNotification } from '../../../actions/appNotifications';

export default function Expenses() {
  const { t } = useTranslation();
  const size = useWindowSize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const initIntersactionRequestRef = useRef(true);
  const IntersactionMarkerRef = useRef(null);
  const { isIntersecting } = useOnScreen(IntersactionMarkerRef, {
    threshold: 0.1
  });

  const expensesList = useSelector((state) => state?.expenses?.data);
  const paginationData = useSelector((state) => state.expenses.pagination);
  const infinityIsLoading = useSelector((state) => state.expenses.pendingInfinityScroll);
  const expensesPagination = useSelector((state) => state.expenses.pagination);
  const expensesPending = useSelector((state) => state.expenses.pending);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const pendingTandECounts = useSelector((state) => state.timesheet?.tandecounts);
  const expenseValidationStatus = useSelector((state) => state?.timesheet?.validate?.expense);
  const filtersOptions = useSelector((state) => state.filtersOptionsFromApi.filters);
  const exCounts = isApprover
    ? {
        counter: 1
        /* TODO need BE to rework response for GET v1/get-tne-count the same as for GET v1/get-tne-count-contractor */
      }
    : useSelector((state) => state.timesheet.contractortandecounts?.Expense);
  const isApproveAllActive = useSelector((state) =>
    state?.expenses?.data?.some((exp) => exp.Status.Status === timesheetsStatuses(t).submitted.apiStatus)
  );
  const deletePending = useSelector((state) => state.expenses.pendingDelete);

  const filters = isApprover ? approverExpensesFilters : contractorExpensesFilters;
  const initPageNumber = Number(query.get('page')) || 1;
  const initDay = query.get('day') || '';
  const initMonth = query.get('month') || '';
  const initYear = query.get('year') || '';
  const initStatus = query.get('status') || '';
  const initClaim = query.get('claim') || '';
  const initFirstName = query.get('FirstName') || '';
  const initLastName = query.get('LastName') || '';
  const [active, setCurrentPage] = useState(initPageNumber);
  const [day, setDay] = useState(initDay);
  const [month, setMonth] = useState(initMonth);
  const [year, setYear] = useState(initYear);
  const [status, setStatus] = useState(initStatus);
  const [claim, setClaim] = useState(initClaim);
  const [contractor, setContractor] = useState({
    FirstName: initFirstName,
    LastName: initLastName
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [openPopUp, setOpenPop] = useState(false);
  const browserLocation = useLocation();
  const itemClickHandler = (id) => navigate(`/dashboard/expense/${id}${browserLocation.search}`);
  const userId = localStorage.getItem('UserId');
  const [lineToDelete, setLineToDelete] = useState('');
  const prevPendingDelete = usePrevious(deletePending);

  const handleSearchParams = () => {
    let oldSearchParams = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of searchParams.entries()) {
      oldSearchParams = {
        ...oldSearchParams,
        [key]: value
      };
    }

    return setSearchParams({
      ...oldSearchParams,
      page: active,
      day,
      month,
      year,
      status,
      claim,
      FirstName: contractor.FirstName,
      LastName: contractor.LastName
    });
  };

  const handlePagination = (num) => {
    setCurrentPage(num);
  };

  const handleMonth = (choosedMonth) => {
    setMonth(choosedMonth);
    if (choosedMonth === t('common.general.all')) setYear('');
    if (choosedMonth && choosedMonth !== t('common.general.all')) setDay('');
  };

  const handleYear = (choosedYear) => {
    setYear(choosedYear);
  };

  const handleDay = (choosedDay) => {
    setDay(choosedDay);
    if (choosedDay) {
      handleMonth('');
      handleYear('');
    }
  };

  const handleStatus = (choosedStatus) => {
    setStatus(choosedStatus);
  };

  const handleClaim = (choosedClaim) => {
    setClaim(choosedClaim);
  };

  const handleContractor = (choosedContractor) => {
    setContractor(choosedContractor);
  };

  const approveAll = async () => {
    const requestArr = [];
    expensesList.forEach((item) => {
      requestArr.push({
        ExpenseId: item.Id,
        Status: timesheetsStatuses(t).approved.apiStatus
      });
    });
    // const res = requestArr.slice(0,APPROVE_ALL_EXPENSES_COUNT)
    console.log('xx', requestArr);
    try {
      await tneAxiosClient.post(`v1/expense-approve-all`, requestArr);
      dispatch(changeExpensesStatus(timesheetsStatuses(t).approved.apiStatus));
      navigate('/dashboard/expenses?page=1&day=&month=&year=&status=Approved&claim=&FirstName=&LastName=');
      setOpenPop('approveAll');
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
  };

  useEffect(() => {
    dispatch(
      getExpenses(
        userId,
        active,
        globalPageSize,
        day,
        month,
        year,
        status,
        claim,
        contractor,
        t,
        isApprover || checkRoleInLS('tAndEApprover'),
        filtersOptions
      )
    );
    handleSearchParams();
  }, [active, day, month, year, status, claim, contractor]);

  useEffect(() => {
    dispatch(clearExpense());
    dispatch(getTimeExpenseValidation('', ''));
  }, []);

  const deleteHandler = (id) => {
    setOpenPop({ modalType: 'delete' });
    setLineToDelete(id);
  };

  const handleDeletePopupHandler = () => {
    dispatch(deleteClaim(lineToDelete));
    setOpenPop('');
  };

  const popUpModals = {
    saved: <SavedPopUp />,
    unsavedChanges: <UnsavedChanges closeHandler={() => setOpenPop(false)} />,
    delete: (
      <DeletePopUp
        deleteHandler={() => handleDeletePopupHandler()}
        closeHandler={() => {
          setOpenPop(false);
        }}
        withBackbtn={false}
        pending={deletePending}
      />
    ),
    approved: <FinalModal message={t('dashboard.expenses.approvedsuccessfully')} />,
    approveAll: (
      <ApproveAllFinalModal
        message={t('dashboard.expenses.approvedsuccessfully')}
        refresh={() => dispatch(getExpenses(isApprover))}
        closeHandler={() => {
          setOpenPop(false);
        }}
      />
    ),
    approve: <ConfirmationModal pendingTandECounts={pendingTandECounts} confirm={approveAll} />,
    rejected: <FinalModal message={t('dashboard.expenses.rejectedsuccessfully')} />,
    reject: <RejectModal />
  };

  useEffect(() => {
    if (
      isIntersecting &&
      size.width < responsiveSizeTimesheet &&
      !initIntersactionRequestRef.current &&
      paginationData &&
      paginationData.PageNumber + 1 <= paginationData?.TotalPages
    ) {
      dispatch(
        getNextExpenses(
          userId,
          Number(paginationData.PageNumber) + 1,
          globalPageSize,
          day,
          month,
          year,
          status,
          claim,
          contractor,
          t,
          isApprover || checkRoleInLS('tAndEApprover'),
          filtersOptions
        )
      );
    }
    initIntersactionRequestRef.current = false;
  }, [isIntersecting, expensesList]);

  useEffect(() => {
    if ((prevPendingDelete && !deletePending) || browserLocation.state?.idToDelete) {
      setOpenPop(false);
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${t('dashboard.expenses.expense')} ${t('common.general.deleted')}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS,
          bottom: '160px',
          action: (
            <p
              role="presentation"
              className={classes.undoBtn}
              onClick={async () => {
                try {
                  await tneAxiosClient.get(
                    `/v1/expense/activate?expenseId=${browserLocation.state?.idToDelete || lineToDelete}`
                  );
                  dispatch(
                    getExpenses(
                      userId,
                      active,
                      globalPageSize,
                      day,
                      month,
                      year,
                      status,
                      claim,
                      contractor,
                      t,
                      isApprover || checkRoleInLS('tAndEApprover'),
                      filtersOptions
                    )
                  );
                  dispatch(removeNotification());
                } catch (e) {
                  basicErrorHandler(dispatch, e.message);
                }
              }}
            >
              {t('common.general.undo')}
            </p>
          )
        })
      );
    }
  }, [prevPendingDelete, deletePending]);

  useEffect(() => {
    dispatch(getOptionsForFilters(appAreas.EXPENSES, isApprover));
  }, [isApprover]);

  if (!expensesPending && !Object.values(exCounts)?.some((counter) => counter > 0))
    return (
      <EmptyRecord
        title={t('dashboard.expenses.title')}
        icon={<ExpensesEmptyIcon />}
        headerLabel={t('dashboard.createclaim.emptyexpenses')}
        startTracking={
          !isApprover ? t('dashboard.createclaim.starttrackingnow') : t('dashboard.createclaim.emptynotification')
        }
        mainButtonLabel={!isApprover ? t('dashboard.expenses.newbuttontxt') : t('common.general.goback')}
        mainButtonHandler={!isApprover ? '/dashboard/create-claim' : '/dashboard'}
        textButtonLabel={t('dashboard.createclaim.gotomaindashboard')}
        textButtonHandler="/dashboard"
        isApprover={isApprover}
      />
    );

  return (
    <section className={classes.mainContainer}>
      <TNEDashboardHeader
        title={size.width < responsiveSizeTimesheet ? t('dashboard.expenses.title') : ''}
        filters={filters}
        values={{
          day,
          month,
          year,
          status,
          claim,
          contractor
        }}
        changeHandlers={{
          handleDay,
          handleMonth,
          handleYear,
          handleStatus,
          handleClaim,
          handleContractor
        }}
        part={appAreas.EXPENSES}
      />
      {size.width > responsiveSizeTimesheet ? (
        <CustomTable
          data={expensesList || []}
          columns={() => columns(t, isApprover, deleteHandler)}
          withFooter={false}
          withLines={{
            header: false,
            body: false,
            footer: false
          }}
          bodyWithBorderRadius={false}
          rowClickHandler={itemClickHandler}
          wrapperStyles={{}}
          headerStyles={{
            borderRadius: '10px  10px 10px 10px',
            padding: '20px 40px 20px 88px'
          }}
          bodyStyles={{
            overflow: 'visible'
          }}
          lineStyles={{
            backgroundColor: colors.WHITE,
            boxShadow: '0 0 30px rgba(61, 107, 149, 0.1)',
            borderRadius: '10px',
            height: '68px',
            marginTop: '12px',
            padding: '20px 40px 20px 88px'
          }}
          isLoading={expensesPending}
          loadingComponent={<TnEListSkeleton qty={6} height="68px" gap="12px" />}
        />
      ) : (
        <div className={classes.cardListWrapper}>
          <TNECardsList
            type="expense"
            data={expensesList}
            clickHandler={itemClickHandler}
            isLoading={expensesPending}
            loadingComponent={<TnEListSkeleton qty={10} height="90px" gap="16px" />}
          />
        </div>
      )}
      <div ref={IntersactionMarkerRef}>
        {infinityIsLoading && <CircularProgress sx={{ color: colors.NAVY_BLUE }} />}
      </div>
      {!isApprover && (
        <div className={classes.addButtonContainer}>
          <Link to="/dashboard/create-claim">
            <div className={classes.innerContainer}>
              {size.width > responsiveSizeTimesheet ? (
                <MainButton
                  variant={expenseValidationStatus ? 'primary' : 'disabled'}
                  disabled={!expenseValidationStatus}
                  rounded
                  forceWidth="max-content"
                  size="medium"
                >
                  {t('dashboard.expenses.newbuttontxt')}
                </MainButton>
              ) : (
                <AddGradientIcon size="50px" />
              )}
            </div>
          </Link>
        </div>
      )}
      {isApprover && pendingTandECounts?.PendingClaimsCount > 0 && (
        <div className={classes.approveButtonContainer}>
          <div className={classes.approveButton}>
            <MainButton
              rounded
              variant={isApproveAllActive ? buttonVariants.PRIMARY : buttonVariants.DISABLED}
              disabled={!isApproveAllActive}
              action={() => setOpenPop('approve')}
              forceWidth="100%"
              size="medium"
            >
              {t('dashboard.expenses.approveallbuttontxt')}
            </MainButton>
          </div>
        </div>
      )}
      {size.width > responsiveSizeTimesheet && (
        <div className={classes.paginationPanelContainer}>
          <StyledPaginationPanel
            total={expensesPagination?.TotalPages}
            active={active}
            setActive={(num) => handlePagination(num)}
          />
        </div>
      )}
      <BaseModal
        open={!!openPopUp}
        fullscreen={false}
        closeHandler={() => setOpenPop('')}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        marginLeftRight={1}
      >
        <WithCloseIcon close={() => setOpenPop('')}>{popUpModals[openPopUp?.modalType]}</WithCloseIcon>
      </BaseModal>
    </section>
  );
}
